import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Users, FileText, BarChart3, MessageSquare, Share2,
    TrendingUp
} from "lucide-react";
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
    ResponsiveContainer, CartesianGrid, Cell
} from "recharts";
import AdminLayout, { AdminPageHead } from "./AdminLayout";
import { request } from "../service/HttpService";
import { isRateLimited } from "../service/HttpErrors";
import { getJwtRole } from "../service/AuthService";
import { admin } from "../constants/Constants";

const BUCKET_LABELS = ["0–20", "21–40", "41–60", "61–80", "81–100"];
const DIST_COLORS = ["#e74c3c", "#e67e22", "#f1c40f", "var(--series-blue)", "var(--accent)"];

function StatCard({ icon: Icon, iconBg, iconColor, value, label }) {
    return (
        <div className="submissions-card" style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
                width: 38, height: 38, borderRadius: 10, background: iconBg,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
            }}>
                <Icon size={18} color={iconColor} />
            </div>
            <div>
                <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--ink-900)", lineHeight: 1.1 }}>
                    {value}
                </p>
                <p style={{ margin: 0, fontSize: 11.5, color: "var(--ink-300)", marginTop: 2 }}>{label}</p>
            </div>
        </div>
    );
}

function ChartCard({ title, subtitle, children }) {
    return (
        <div className="submissions-card" style={{ padding: "20px" }}>
            <p style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: "var(--ink-900)" }}>{title}</p>
            {subtitle && <p style={{ margin: 0, fontSize: 12, color: "var(--ink-300)", marginTop: 2 }}>{subtitle}</p>}
            <div style={{ marginTop: 16 }}>
                {children}
            </div>
        </div>
    );
}

function formatDate(dateStr) {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function Analytics() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (getJwtRole() !== admin) {
            navigate("/");
            return;
        }
        request("GET", "/admin/analytics")
            .then(res => {
                setData(res);
                setLoading(false);
            })
            .catch(err => isRateLimited(err) ? setLoadError(err.message) : navigate("/login"));
    }, []);

    if (loading) {
        return (
            <AdminLayout>
                        <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--ink-300)", fontSize: 13 }}>{loadError || "Loading analytics..."}</div>
            </AdminLayout>
        );
    }

    const noveltyDist = (data.noveltyDistribution ?? []).map((v, i) => ({ range: BUCKET_LABELS[i], count: v }));
    const methodDist = (data.methodDistribution ?? []).map((v, i) => ({ range: BUCKET_LABELS[i], count: v }));
    const impactDist = (data.impactDistribution ?? []).map((v, i) => ({ range: BUCKET_LABELS[i], count: v }));

    const signups = (data.signupsByDay ?? []).map(d => ({ ...d, label: formatDate(d.date) }));
    const uploads = (data.uploadsByDay ?? []).map(d => ({ ...d, label: formatDate(d.date) }));

    const statusData = [
        { name: "Completed", value: data.completedManuscripts ?? 0 },
        { name: "Processing", value: (data.inProcessManuscripts ?? 0) + (data.newManuscripts ?? 0) },
        { name: "Errored", value: data.erroredManuscripts ?? 0 },
    ];
    const STATUS_COLORS = ["var(--accent)", "var(--series-blue)", "#e74c3c"];

    return (
        <AdminLayout>

                    <AdminPageHead
                        title="User Analytics"
                        subtitle="Platform-wide usage and scoring insights"
                    />

                    {/* Top-level stats */}
                    <div className="analytics-stats-row">
                        <StatCard icon={Users} iconBg="var(--blue-bg)" iconColor="var(--blue)"
                            value={data.totalUsers ?? 0} label="Total Users" />
                        <StatCard icon={FileText} iconBg="var(--accent-soft)" iconColor="#22c55e"
                            value={data.totalManuscripts ?? 0} label="Manuscripts" />
                        <StatCard icon={TrendingUp} iconBg="var(--warm-bg)" iconColor="var(--bronze)"
                            value={data.avgOverallScore ?? 0} label="Avg Score" />
                        <StatCard icon={BarChart3} iconBg="var(--purple-bg)" iconColor="var(--violet)"
                            value={data.totalReports ?? 0} label="Reports" />
                        <StatCard icon={Share2} iconBg="var(--danger-muted-bg)" iconColor="#e74c3c"
                            value={data.sharedReports ?? 0} label="Shared" />
                        <StatCard icon={MessageSquare} iconBg="var(--accent-soft)" iconColor="#0ea5e9"
                            value={data.totalFeedback ?? 0} label="Feedback" />
                    </div>

                    {/* Average scores */}
                    <div className="analytics-scores-row">
                        {[
                            { label: "Avg Novelty", value: data.avgNoveltyScore, color: "var(--accent-text)" },
                            { label: "Avg Methodology", value: data.avgMethodScore, color: "var(--series-blue)" },
                            { label: "Avg Impact", value: data.avgImpactScore, color: "var(--bronze)" },
                            { label: "Avg Overall", value: data.avgOverallScore, color: "var(--brand-text)" },
                        ].map(s => (
                            <div key={s.label} className="submissions-card" style={{ padding: "16px 20px", textAlign: "center" }}>
                                <p style={{ margin: 0, fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1.1 }}>
                                    {s.value ?? 0}
                                </p>
                                <p style={{ margin: 0, fontSize: 11.5, color: "var(--ink-300)", marginTop: 4 }}>{s.label}</p>
                            </div>
                        ))}
                    </div>

                    {/* Charts row 1: Signups + Uploads over time */}
                    <div className="analytics-charts-row">
                        <ChartCard title="User Signups" subtitle="New registrations over time">
                            {signups.length === 0 ? (
                                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>No data yet</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={signups} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                                        <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--ink-900)" }} />
                                        <Line type="monotone" dataKey="count" stroke="var(--blue)" strokeWidth={2} dot={false} name="Signups" />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </ChartCard>

                        <ChartCard title="Manuscript Uploads" subtitle="Papers uploaded over time">
                            {uploads.length === 0 ? (
                                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>No data yet</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={uploads} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                                        <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--ink-900)" }} />
                                        <Line type="monotone" dataKey="count" stroke="var(--accent)" strokeWidth={2} dot={false} name="Uploads" />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </ChartCard>
                    </div>

                    {/* Charts row 2: Score distributions */}
                    <div className="analytics-charts-row analytics-charts-row-3">
                        <ChartCard title="Novelty Distribution" subtitle="Score spread across manuscripts">
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={noveltyDist} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                                    <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                    <XAxis dataKey="range" tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--ink-900)" }} itemStyle={{ color: "var(--ink-900)" }} cursor={{ fill: "var(--ink-200)" }} />
                                    <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Papers">
                                        {noveltyDist.map((_, i) => <Cell key={i} fill={DIST_COLORS[i]} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>

                        <ChartCard title="Methodology Distribution" subtitle="Score spread across manuscripts">
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={methodDist} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                                    <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                    <XAxis dataKey="range" tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--ink-900)" }} itemStyle={{ color: "var(--ink-900)" }} cursor={{ fill: "var(--ink-200)" }} />
                                    <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Papers">
                                        {methodDist.map((_, i) => <Cell key={i} fill={DIST_COLORS[i]} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>

                        <ChartCard title="Impact Distribution" subtitle="Score spread across manuscripts">
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={impactDist} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                                    <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" vertical={false} />
                                    <XAxis dataKey="range" tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--ink-900)" }} itemStyle={{ color: "var(--ink-900)" }} cursor={{ fill: "var(--ink-200)" }} />
                                    <Bar dataKey="count" radius={[4, 4, 0, 0]} name="Papers">
                                        {impactDist.map((_, i) => <Cell key={i} fill={DIST_COLORS[i]} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>

                    {/* Manuscript status + Top uploaders */}
                    <div className="analytics-charts-row">
                        <ChartCard title="Manuscript Status" subtitle="Breakdown by processing state">
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={statusData} layout="vertical" margin={{ top: 4, right: 12, left: 10, bottom: 0 }}>
                                    <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="4 4" horizontal={false} />
                                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-300)" }} axisLine={false} tickLine={false} />
                                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "var(--ink-500)" }} axisLine={false} tickLine={false} width={80} />
                                    <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--ink-900)" }} itemStyle={{ color: "var(--ink-900)" }} cursor={{ fill: "var(--ink-200)" }} />
                                    <Bar dataKey="value" radius={[0, 4, 4, 0]} name="Manuscripts">
                                        {statusData.map((_, i) => <Cell key={i} fill={STATUS_COLORS[i]} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>

                        <ChartCard title="Top Uploaders" subtitle="Most active users by uploads">
                            {(data.topUploaders ?? []).length === 0 ? (
                                <p style={{ color: "var(--ink-300)", fontSize: 13, textAlign: "center", padding: 20 }}>No data yet</p>
                            ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                    {(data.topUploaders ?? []).map((u, i) => (
                                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <span style={{
                                                width: 24, height: 24, borderRadius: "50%", background: "var(--blue-bg)",
                                                color: "var(--blue)", display: "flex", alignItems: "center", justifyContent: "center",
                                                fontSize: 11, fontWeight: 600, flexShrink: 0
                                            }}>{i + 1}</span>
                                            <span style={{ flex: 1, fontSize: 13, color: "var(--ink-900)", fontWeight: 500 }}>{u.name}</span>
                                            <span style={{
                                                fontSize: 12, color: "var(--ink-500)", background: "var(--surface-3)",
                                                padding: "2px 10px", borderRadius: 6, fontWeight: 500
                                            }}>{u.count}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </ChartCard>
                    </div>

        </AdminLayout>
    );
}
